// ============================================================================
// nomsPersonnes.js : l'écriture des prénoms et des noms des fiches membres
// (delegates/). Règle de Pierre du 30/09/2026 : « Prénom » « NOM ».
//
//   Prénom : chaque segment séparé par une espace ou un trait d'union prend
//            une majuscule initiale, le reste en minuscules, accents gardés
//            (« JEAN-MARIE » → « Jean-Marie », « marie-hélène » → « Marie-Hélène »).
//   NOM    : tout en capitales, accents gardés (toLocaleUpperCase("fr") :
//            « Hérault » → « HÉRAULT »), espaces et traits d'union gardés.
//   Les deux : espaces multiples réduites, espaces de bord retirées, forme
//            Unicode composée (NFC : un « é » saisi en deux caractères devient
//            le même « é » que les autres, pour la recherche).
//
// CE QUI N'EST JAMAIS TRANCHÉ AUTOMATIQUEMENT (« à vérifier ») : particules
// (de, du, des, d', le, la, van, von, di, da, do, del, el, ould, ter…), y
// compris entre traits d'union (« Jean-de-Dieu », « Abd-el-Kader »), et, hors
// de toute liste, un mot en minuscule après une espace dans un champ qui porte
// aussi une majuscule (« Maria do Céu », « af Klint », « Dupont et Martin » :
// une minuscule VOULUE, que la casse de titre des PV effacerait), apostrophes
// (d'Harcourt, O'Brien, N'Diaye), majuscules internes (McDonald, MacLeod,
// JoAnne), noms d'usage (épouse, née, veuve, dit…), prénom ou nom vide, les
// deux dans un seul champ, le prénom répété dans le nom ou l'inverse, prénom
// et nom peut-être inversés (prénom en capitales, nom en minuscules),
// civilités et titres (M., Mme, Me, Dr, Sœur…), initiales et sigles (« L. »,
// « J.-P. », « JP »), caractères hors lettres, libellés qui ne sont pas des
// noms (« Assistante », « SANS-MAIL », « Secrétariat général »), lettres qui
// changent de forme en capitales (« ß » → « SS »), tirets typographiques ou
// espaces autour d'un trait d'union.
// Pourquoi : la grammaire des PV (pvGrammaire.casseNom, documents
// intouchables) laisse un nom mixte tel quel mais repasse un NOM en capitales
// en casse de titre. Toute information que les capitales effacent y
// reparaîtrait fausse : « de La Tour » → « De La Tour », « McDonald » →
// « Mcdonald », « Dupont épouse Martin » → « Dupont Épouse Martin ». Une
// particule DÉJÀ en capitale dans le nom (« Le Bihan », « Da Silva ») ne perd
// rien (casseNom rend « Le Bihan ») : elle n'est pas signalée ; dans un
// prénom, toute particule l'est (la casse de titre la capitaliserait).
//
// Trois usages :
//   • miseEnFormeSaisie(fiche, avant) : saveDelegate, à l'ENREGISTREMENT. Seul
//     un champ SAISI (différent de la fiche en base, ou fiche neuve) est mis en
//     forme, et seulement s'il est sûr ; un cas à vérifier garde la saisie
//     (espaces nettoyées). Une recopie à l'identique (Ma semaine, favoris…) n'y
//     touche jamais : aucune écriture de nom à l'ouverture d'un écran.
//   • analyserIdentite(fiche) : l'outil « Harmoniser l'écriture des noms »
//     (Gestion des membres, harmonisationNoms.js) : proposition, changements,
//     cas à vérifier.
//   • formaterIdentite(fiche) : « Prénom NOM » pour un affichage, prudent
//     comme l'enregistrement (aucun helper central d'affichage n'existe au
//     30/09/2026 ; les générateurs de documents ne passent PAS par ici).
// Module pur, sans dépendance : il entre dans le bundle principal.
// Tests : src/nomsPersonnes.test.js.
// ============================================================================

// Tirets typographiques remplacés par le trait d'union ASCII (U+2010 à U+2015,
// signe moins U+2212).
const TIRETS = /[\u2010-\u2015\u2212]/g;
// Marques combinantes (accents décomposés).
const MARQUES = /[\u0300-\u036f]/;
const MARQUES_G = /[\u0300-\u036f]/g;
// Ponctuation admise dans un nom : espace, trait d'union, apostrophes, point.
const PONCTUATION_ADMISE = new Set([" ", "-", "'", "\u2019", "."]);

export const PARTICULES = Object.freeze([
  "de", "du", "des", "d", "le", "la", "les", "l", "van", "von", "der", "den",
  "di", "da", "del", "della", "dei", "degli", "dal", "dalla", "delle", "lo",
  "do", "dos", "das", "ter", "ten", "het", "op", "zu", "zur", "zum", "vom", "am",
  "af", "av", "el", "al", "ben", "bin", "ibn", "ould", "ag",
]);
const PARTICULES_SET = new Set(PARTICULES);

// Mots de liaison d'un nom d'usage (« Dupont épouse Martin »), sans accents.
export const MOTS_NOM_USAGE = Object.freeze([
  "epouse", "ep", "epse", "nee", "veuve", "vve", "dit", "dite", "alias",
]);
const NOM_USAGE_SET = new Set(MOTS_NOM_USAGE);

// Civilités et titres, sans accents ni point. SEULS : signalés même seuls
// dans le champ ; DEVANT : seulement suivis d'un autre mot (« Pere » est aussi
// un prénom catalan).
const TITRES_SEULS = new Set([
  "m", "mr", "mme", "mmes", "mlle", "me", "dr", "pr", "mgr",
  "monsieur", "madame", "mademoiselle", "maitre", "docteur", "professeur",
]);
const TITRES_DEVANT = new Set(["soeur", "sœur", "frere", "pere", "abbe", "monseigneur", "pasteur", "sr"]);

// Libellés relevés dans les fiches qui ne sont pas des noms de personne.
// Comparés au champ ENTIER, en minuscules et sans accents. Pas « na » : c'est
// aussi un nom coréen et un prénom chinois (Li Na).
export const LIBELLES_NON_NOMS = Object.freeze([
  "assistante", "assistant", "secretariat", "secretaire", "sans-mail", "sans mail",
  "sansmail", "test", "essai", "inconnu", "inconnue", "anonyme", "divers", "contact",
  "direction", "accueil", "standard", "nc", "x", "xx", "xxx", "a completer",
  "provisoire", "invite", "invitee", "admin", "administrateur", "compte", "demo",
  "service", "presidence",
]);
const LIBELLES_SET = new Set(LIBELLES_NON_NOMS);
// Mots qui, au milieu d'un champ de plusieurs mots, en font un libellé
// (« Secrétariat général », « Assistante direction »).
const LIBELLES_MOTS = new Set([
  "assistante", "assistant", "secretariat", "secretaire", "direction", "accueil",
  "standard", "service", "presidence", "administrateur", "administratrice",
  "test", "essai", "inconnu", "inconnue", "anonyme", "provisoire", "mutuelle",
  "sans-mail", "sansmail",
]);

export const MESSAGES_ALERTES = Object.freeze({
  "prenom-vide": "Prénom vide",
  "nom-vide": "Nom vide",
  "deux-dans-un": "Prénom et nom peut-être saisis dans le même champ",
  "inversion": "Prénom et nom peut-être inversés (prénom en capitales)",
  "doublon": "Le nom semble répété dans le prénom",
  "doublon-nom": "Le prénom semble répété dans le nom",
  "titre": "Civilité ou titre (M., Mme, Dr, Sœur…) à retirer",
  "particule": "Particule (de, do, el, van…), à écrire comme la personne l'écrit",
  "minuscule": "Mot en minuscule parmi des majuscules (af, bint, et…), à écrire comme la personne l'écrit",
  "casse-interne": "Majuscule à l'intérieur d'un mot (McDonald, JoAnne, IJsbrand), perdue en capitales",
  "nom-usage": "Nom d'usage (épouse, née, veuve, dit…), à écrire comme la personne l'écrit",
  "apostrophe": "Apostrophe (d', O', N'…), la casse de la lettre qui suit se perd en capitales (Le Floc'h, d'Harcourt)",
  "initiale": "Initiale ou abréviation",
  "sigle": "Sigle ou initiales accolées (JP, JB…)",
  "caracteres": "Caractères inattendus (chiffres, symboles…)",
  "libelle": "Ne ressemble pas à un nom de personne",
  "eszett": "« ß » devient « SS » en capitales",
  "transformation": "Une lettre change de forme en capitales",
  "tiret": "Tiret ou espaces autour d'un trait d'union corrigés",
});

const texte = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Une lettre : tout caractère qui a une casse (latin, grec, cyrillique…),
 *  ou une marque combinante. Sans \p{L} : le transpileur en ferait des Ko de
 *  plages dans le bundle principal. */
export function estLettre(c) {
  return c.toLowerCase() !== c.toUpperCase() || MARQUES.test(c);
}

/** Espaces réduites et retirées aux bords, forme Unicode composée. */
export function nettoyerEspaces(v) {
  return texte(v).normalize("NFC").replace(/\s+/g, " ").trim();
}

// Nettoyage + tirets : ce que la mise en forme part d'un nom saisi.
function preparer(v) {
  return nettoyerEspaces(v).replace(TIRETS, "-").replace(/ ?- ?/g, "-");
}

/** « jean-MARIE  » → « Jean-Marie » ; « j.-p. » → « J.-P. ». */
export function formaterPrenom(v) {
  const t = preparer(v).toLocaleLowerCase("fr");
  let out = "";
  let debut = true;
  for (const c of t) {
    if (c === " " || c === "-") { out += c; debut = true; continue; }
    if (debut && estLettre(c)) { out += c.toLocaleUpperCase("fr"); debut = false; continue; }
    out += c;
  }
  return out;
}

/** « Hérault » → « HÉRAULT » ; « le  roux » → « LE ROUX ». */
export function formaterNom(v) {
  return preparer(v).toLocaleUpperCase("fr");
}

// ── Prédicats ───────────────────────────────────────────────────────────────
const sansAccents = (s) => s.normalize("NFD").replace(MARQUES_G, "");
const nbLettres = (s) => Array.from(s).filter(estLettre).length;
const enCapitales = (s) => s !== s.toLocaleLowerCase("fr") && s === s.toLocaleUpperCase("fr");
const aDesMinuscules = (s) => s !== s.toLocaleUpperCase("fr");
const segments = (s) => s.split(/[ -]+/).filter(Boolean);
const mots = (s) => s.split(" ").filter(Boolean);
const cle = (s) => sansAccents(s).toLowerCase();
const estMinuscule = (c) => c !== c.toLocaleUpperCase("fr") && c === c.toLocaleLowerCase("fr");
const estMajuscule = (c) => c !== c.toLocaleLowerCase("fr") && c === c.toLocaleUpperCase("fr");
const commenceParMinuscule = (seg) => estMinuscule(Array.from(seg)[0] || "");

function caracteresInattendus(s) {
  return Array.from(s).some((c) => !estLettre(c) && !PONCTUATION_ADMISE.has(c));
}
function estLibelle(s) {
  if (!s) return false;
  if (LIBELLES_SET.has(cle(s))) return true;
  const m = mots(s);
  return m.length > 1 && m.some((x) => LIBELLES_MOTS.has(cle(x)));
}
// Premier mot : une civilité ou un titre (« M. », « Me Anne », « Sœur Marie »).
function aTitre(s, { seulAdmis }) {
  const m = mots(s);
  if (!m.length) return false;
  const t = cle(m[0]).replace(/\.$/, "");
  if (TITRES_DEVANT.has(t)) return m.length > 1;
  if (!TITRES_SEULS.has(t)) return false;
  return m.length > 1 || (seulAdmis && (m[0].endsWith(".") || m.length === 1));
}
function aInitiale(s) {
  return s.includes(".") || segments(s).some((seg) => Array.from(seg).length === 1 && estLettre(seg));
}
// Un segment de deux lettres ou plus sans voyelle : « JP », « JB ».
function estSigle(s) {
  return segments(s).some((seg) => {
    const l = sansAccents(seg).toLowerCase().replace(/[^a-zæœ]/g, "");
    return l.length >= 2 && !/[aeiouyæœ]/.test(l);
  });
}
// Particule : un segment (entre espaces OU traits d'union : « Jean-de-Dieu »)
// de la liste, ou une élision « d' » / « l' ». `minusculeSeule` (le NOM) : une
// particule déjà en capitale ne perd rien en capitales, elle ne compte pas.
function aParticule(s, { minusculeSeule = false } = {}) {
  const segs = segments(s);
  const retenue = (m) => !minusculeSeule || commenceParMinuscule(m);
  if (segs.some((m) => /^[dl]['\u2019]/i.test(m) && retenue(m))) return true;
  if (segs.length < 2) return false;
  return segs.some((m) => PARTICULES_SET.has(cle(m).replace(/['\u2019]$/, "")) && retenue(m));
}
// Majuscule interne : dans un segment qui commence par une capitale, une
// minuscule suivie d'une capitale (McDonald, MacLeod, JoAnne, DeShawn), ou le
// digramme néerlandais « IJ » suivi d'une minuscule (IJsbrand, IJzerman). Un
// segment qui commence en minuscule (« éLODIE ») est une frappe en capitales
// inversées, pas une graphie voulue.
function aCasseInterne(s) {
  return segments(s).some((seg) => {
    const cs = Array.from(seg);
    if (!estMajuscule(cs[0] || "")) return false;
    if (cs[0] === "I" && cs[1] === "J" && estMinuscule(cs[2] || "")) return true;
    for (let i = 1; i < cs.length; i += 1) {
      if (estMinuscule(cs[i - 1]) && estMajuscule(cs[i])) return true;
    }
    return false;
  });
}
// Minuscule voulue : un mot (entre ESPACES) qui commence par une minuscule,
// quand un autre mot du champ commence par une capitale (« Maria do Céu »,
// « af Klint », « Mac an Bhaird », « Dupont et Martin »). Filet hors de toute
// liste de particules. Pas entre traits d'union : « Jean-luc », « Marie-claire »
// sont des frappes rapides que la règle corrige sans perte.
function aMinusculeVoulue(s) {
  const m = mots(s);
  if (m.length < 2) return false;
  const commenceParMajuscule = (x) => estMajuscule(Array.from(x)[0] || "");
  return m.some(commenceParMinuscule) && m.some(commenceParMajuscule);
}
function aNomUsage(s) {
  return segments(s).some((m) => NOM_USAGE_SET.has(cle(m).replace(/\.$/, "")));
}
// `a` (plusieurs mots) contient `b` : un mot identique, ou `a` commence par
// `b` suivi d'une espace (« Jean Paul Durand » contient « Jean Paul »).
function repeteDans(a, b) {
  const m = mots(a);
  if (m.length < 2 || !b) return false;
  return m.some((x) => cle(x) === cle(b)) || cle(a).startsWith(`${cle(b)} `);
}

/**
 * Analyse d'une fiche { prenom, nom }.
 * @returns {{ avant:{prenom,nom}, apres:{prenom,nom}, changements:{prenom,nom},
 *   change:boolean, alertes:Array<{champ,code,message}>, sur:boolean }}
 *   `avant` : les valeurs brutes (chaînes) ; `apres` : la proposition
 *   mécanique ; `sur` : un changement sans aucune alerte. Une alerte porte
 *   sur "prenom", "nom" ou "identite" (les deux champs ensemble).
 *   Alertes de FOND (toujours) : vide, deux dans un champ, inversion, doublons,
 *   titre, caractères, libellé. Alertes de FORME (seulement si le champ
 *   change) : particule, apostrophe, majuscule interne, nom d'usage, initiale,
 *   sigle, ß, transformation, tiret, minuscule voulue (filet, en dernier).
 */
export function analyserIdentite(fiche) {
  const avant = { prenom: texte(fiche && fiche.prenom), nom: texte(fiche && fiche.nom) };
  const propre = { prenom: nettoyerEspaces(avant.prenom), nom: nettoyerEspaces(avant.nom) };
  const prepare = { prenom: preparer(avant.prenom), nom: preparer(avant.nom) };
  const apres = { prenom: formaterPrenom(avant.prenom), nom: formaterNom(avant.nom) };
  const changements = { prenom: apres.prenom !== avant.prenom, nom: apres.nom !== avant.nom };
  const alertes = [];
  const ajouter = (champ, code) => {
    if (!alertes.some((a) => a.champ === champ && a.code === code)) {
      alertes.push({ champ, code, message: MESSAGES_ALERTES[code] });
    }
  };

  // Fond.
  if (!propre.prenom) ajouter("prenom", "prenom-vide");
  if (!propre.nom) ajouter("nom", "nom-vide");
  if (!propre.prenom && propre.nom.includes(" ")) ajouter("nom", "deux-dans-un");
  if (!propre.nom && propre.prenom.includes(" ")) ajouter("prenom", "deux-dans-un");
  ["prenom", "nom"].forEach((champ) => {
    // Sur la forme préparée : un tiret typographique relève de « tiret ».
    if (caracteresInattendus(prepare[champ])) ajouter(champ, "caracteres");
    if (estLibelle(propre[champ])) ajouter(champ, "libelle");
    if (aTitre(propre[champ], { seulAdmis: champ === "prenom" })) ajouter(champ, "titre");
  });
  // Un prénom à initiales (« J.-P. ») n'est pas un nom en capitales.
  if (enCapitales(propre.prenom) && nbLettres(propre.prenom) >= 2 && !estSigle(propre.prenom)
      && !propre.prenom.includes(".") && aDesMinuscules(propre.nom)) {
    ajouter("identite", "inversion");
  }
  if (repeteDans(propre.prenom, propre.nom)) ajouter("prenom", "doublon");
  if (repeteDans(propre.nom, propre.prenom)) ajouter("nom", "doublon-nom");

  // Forme : seulement sur un champ que la proposition change.
  ["prenom", "nom"].forEach((champ) => {
    if (!changements[champ]) return;
    const s = propre[champ];
    if (/['\u2019]/.test(s)) ajouter(champ, "apostrophe");
    if (aInitiale(s)) ajouter(champ, "initiale");
    if (/[\u00df\u1e9e]/.test(s)) ajouter(champ, "eszett");
    else if (Array.from(apres[champ]).length !== Array.from(prepare[champ]).length) ajouter(champ, "transformation");
    if (prepare[champ] !== s) ajouter(champ, "tiret");
    if (aCasseInterne(s)) ajouter(champ, "casse-interne");
  });
  // Particule : dans le nom quand elle est en minuscule (« de La Tour »,
  // « dupont-de-la-tour ») ; dans un prénom, toujours (« Marie de Lourdes »,
  // « Jean-de-Dieu » ne deviennent pas « Marie De Lourdes », « Jean-De-Dieu »).
  if (changements.nom && aParticule(propre.nom, { minusculeSeule: true })) ajouter("nom", "particule");
  if (changements.prenom && aParticule(propre.prenom)) ajouter("prenom", "particule");
  if (changements.nom && aNomUsage(propre.nom)) ajouter("nom", "nom-usage");
  if (changements.prenom && estSigle(propre.prenom)) ajouter("prenom", "sigle");
  // Filet en dernier : seulement si rien d'autre ne retient déjà le champ.
  ["prenom", "nom"].forEach((champ) => {
    if (!changements[champ] || alertes.some((a) => a.champ === champ || a.champ === "identite")) return;
    if (aMinusculeVoulue(propre[champ])) ajouter(champ, "minuscule");
  });

  const change = changements.prenom || changements.nom;
  return { avant, apres, changements, change, alertes, sur: change && alertes.length === 0 };
}

/** Le champ peut-il être mis en forme sans décision humaine ? */
export function champSur(analyse, champ) {
  return !analyse.alertes.some((a) => a.champ === champ || a.champ === "identite");
}

// Valeur « prudente » d'un champ : la mise en forme s'il est sûr, sinon la
// saisie aux espaces près (aussi le préremplissage de l'outil d'harmonisation).
export function valeurPrudente(analyse, champ) {
  return champSur(analyse, champ) ? analyse.apres[champ] : nettoyerEspaces(analyse.avant[champ]);
}

/**
 * Pour saveDelegate : les champs prenom/nom à RÉÉCRIRE dans la fiche qui part,
 * ou {} s'il n'y a rien à changer. Un champ absent de `fiche`, ou identique à
 * celui de `avant` (la fiche en base), n'est jamais touché. `avant` null :
 * fiche neuve, tout champ porté est une saisie.
 */
export function miseEnFormeSaisie(fiche, avant) {
  const out = {};
  if (!fiche || typeof fiche !== "object") return out;
  const analyse = analyserIdentite(fiche);
  ["prenom", "nom"].forEach((champ) => {
    const v = fiche[champ];
    if (typeof v !== "string") return;
    if (avant && typeof avant === "object" && avant[champ] === v) return;
    const r = valeurPrudente(analyse, champ);
    if (r !== v) out[champ] = r;
  });
  return out;
}

const CHAMPS_LIBELLES = { prenom: "Prénom", nom: "Nom" };
const ALERTES_SANS_CHAMP = new Set(["prenom-vide", "nom-vide"]);
/** Le motif d'une alerte avec le champ qu'il vise : « Nom : particule… ». */
export function libelleAlerte(alerte) {
  const m = (alerte && alerte.message) || "";
  const champ = alerte && CHAMPS_LIBELLES[alerte.champ];
  if (!champ || ALERTES_SANS_CHAMP.has(alerte.code) || !m) return m;
  return `${champ}\u00a0: ${m.charAt(0).toLocaleLowerCase("fr")}${m.slice(1)}`;
}

/** « Prénom NOM » pour un affichage : formaterIdentite({ prenom, nom }) ou
 *  formaterIdentite(prenom, nom). Prudent : un cas à vérifier s'affiche tel
 *  qu'il est écrit (espaces nettoyées). */
export function formaterIdentite(prenomOuFiche, nom) {
  const fiche = prenomOuFiche && typeof prenomOuFiche === "object"
    ? prenomOuFiche : { prenom: prenomOuFiche, nom };
  const analyse = analyserIdentite(fiche);
  return [valeurPrudente(analyse, "prenom"), valeurPrudente(analyse, "nom")].filter(Boolean).join(" ");
}
